import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, rectSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Upload, Trash2, GripVertical, Save, Loader2, ImagePlus, Search, ImageIcon,
  LogOut, BedDouble, CheckSquare, Square, X, LibraryBig, BookOpen, Sparkles,
  ImageDown, Database,
} from "lucide-react";
import { buildHotelRoomTree } from "@/lib/roomRegistry";
import { getHotelDetails } from "@/lib/hotelDetails";
import { EXPERIENCES } from "@/lib/data";
import { safeReadBody } from "@/lib/fetchWithRetry";
import FileDropZone, { UploadProgress } from "@/components/admin/FileDropZone";
import MediaPicker from "@/components/admin/MediaPicker";
import BunnyStoragePicker from "@/components/admin/BunnyStoragePicker";

const API = process.env.REACT_APP_BACKEND_URL || "";

const pick = (obj) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj.en || obj.fr || obj.es || "";
};

// ────────────────────────────────────────────────────────────────────────
// Gallery targets — every editable image collection on the site is one of:
//
//   target.kind === "room"            → existing room gallery
//   target.kind === "property-hero"   → 1-image hero for a property section
//   target.kind === "property-gallery"→ multi-image gallery for a section
//
// `section` ∈ "about" + every service slug present in `getHotelDetails(slug)`.
// ────────────────────────────────────────────────────────────────────────
const buildEndpoints = (target) => {
  if (!target) return null;
  if (target.kind === "property-hero") {
    return {
      base: `/api/admin/property-galleries/${target.hotelSlug}/${target.sectionSlug}/hero`,
      pickerTitle: `Pick hero image for ${target.sectionLabel}`,
      isHero: true,
    };
  }
  if (target.kind === "property-gallery") {
    return {
      base: `/api/admin/property-galleries/${target.hotelSlug}/${target.sectionSlug}/gallery`,
      pickerTitle: `Add to ${target.sectionLabel} gallery`,
      isHero: false,
    };
  }
  // Default: room gallery
  return {
    base: `/api/admin/galleries/${target.hotelSlug}/${target.slug}`,
    pickerTitle: "Add to this room gallery",
    isHero: false,
  };
};

const resolveImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

// Build the list of property sections for a hotel. Always starts with
// "about", then appends every service the hotel defines (sourced from
// `hotelDetails.js`). Returns `[{slug, label, icon}]`.
const ICON_BY_SLUG = {
  "spa-hammam": Sparkles,
  "bars-restaurants": Sparkles,
  "swimming-area": Sparkles,
  "sports-area": Sparkles,
  "conference-room": Sparkles,
  shop: Sparkles,
  "concierge-transfers": Sparkles,
};
const buildPropertySections = (hotelSlug) => {
  const d = getHotelDetails ? getHotelDetails(hotelSlug) : null;
  const services = (d?.services || []).filter((s) => s.slug);
  return [
    { slug: "about", label: "About the Hotel", icon: BookOpen, kind: "about" },
    ...services.map((s) => ({
      slug: s.slug,
      label: pick(s.title) || s.slug,
      icon: ICON_BY_SLUG[s.slug] || Sparkles,
      kind: "service",
    })),
  ];
};

// ────────────────────────────────────────────────────────────────────────
// Password gate
// ────────────────────────────────────────────────────────────────────────
const PasswordGate = ({ onUnlock }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setError("Please enter the password.");
      setBusy(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/session/login`, {
      credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: trimmed }),
      });
      if (!res.ok) {
        setError(res.status === 404
          ? "Admin endpoints not found on this server. Please redeploy the backend."
          : res.status === 429
            ? "Too many attempts. Try again in a few minutes."
            : "Incorrect password.");
        setBusy(false);
        return;
      }
      // Server sets the httpOnly cookie. No client-side storage.
      onUnlock();
    } catch (err) {
      setError(err.message || "Network error");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[#2C2621]/10 p-8 md:p-10"
        data-testid="admin-galleries-password-form"
      >
        <p className="overline text-[#C16542]">Galleries · Admin</p>
        <h1 className="font-serif-x text-3xl md:text-4xl mt-3 tracking-tight">
          Enter the gallery password
        </h1>
        <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">
          Manage every room-type gallery across the group. Single shared password — no accounts.
        </p>
        <label className="block mt-7 text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
          Password
        </label>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          className="mt-2 w-full border border-[#2C2621]/15 px-4 py-3.5 text-base focus:outline-none focus:border-[#C16542]"
          data-testid="admin-galleries-password-input"
        />
        {error && (
          <p className="mt-3 text-sm text-[#C16542]" data-testid="admin-galleries-password-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !value}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 text-[#FDFBF7] px-6 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
          data-testid="admin-galleries-password-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Unlock
        </button>
      </form>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Sortable image card
// ────────────────────────────────────────────────────────────────────────
const SortableImageCard = ({ image, onDelete, isSelected, onToggleSelect, anySelected }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: image.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const resolvedUrl = image.url.startsWith("/api/") ? `${API}${image.url}` : image.url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "group relative bg-[#FDFBF7] border transition-colors " +
        (isSelected
          ? "border-[#C16542] ring-2 ring-[#C16542]/30"
          : "border-[#2C2621]/10 hover:border-[#C16542]/40")
      }
      data-testid={`gallery-image-${image.id}`}
      data-selected={isSelected ? "true" : "false"}
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#F2EBE1]">
        <img
          src={resolvedUrl}
          alt={image.alt || ""}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelect(image.id); }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={isSelected ? "Deselect image" : "Select image"}
        aria-pressed={isSelected}
        className={
          "absolute top-2 left-2 z-10 inline-flex items-center justify-center w-7 h-7 rounded-sm shadow-sm border transition-opacity " +
          (isSelected
            ? "bg-[#C16542] text-[#FDFBF7] border-[#C16542] opacity-100"
            : "bg-white/95 text-[#2C2621] border-[#2C2621]/20 " +
              (anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"))
        }
        data-testid={`gallery-image-checkbox-${image.id}`}
      >
        {isSelected ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className="w-4 h-4" strokeWidth={1.8} />}
      </button>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-[#2C2621]/10">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] cursor-grab active:cursor-grabbing"
          data-testid={`gallery-image-drag-${image.id}`}
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" strokeWidth={1.6} />
          Reorder
        </button>
        <button
          type="button"
          onClick={() => onDelete(image.id)}
          className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542]"
          data-testid={`gallery-image-delete-${image.id}`}
          aria-label="Delete image"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
          Delete
        </button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Gallery editor (right pane) — handles room galleries AND the multi-image
// "gallery" half of a property section. The hero half is rendered above
// by `HeroEditor` using its own independent backend endpoints, so anything
// done here is guaranteed not to touch the hero collection.
// ────────────────────────────────────────────────────────────────────────
const GalleryEditor = ({ target, maxImages = null }) => {
  const [images, setImages] = useState([]);
  const [initialImages, setInitialImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bunnyOpen, setBunnyOpen] = useState(false);
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);
  const [addingFromBunny, setAddingFromBunny] = useState(false);

  const endpoints = useMemo(() => buildEndpoints(target), [target]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const dirty = useMemo(() => {
    if (images.length !== initialImages.length) return true;
    return images.some((img, i) => img.id !== initialImages[i]?.id);
  }, [images, initialImages]);

  // Auth carried via httpOnly cookie; headers stays empty for callers that
  // spread it into fetch options.
  const headers = useMemo(() => ({}), []);

  // Load gallery
  useEffect(() => {
    if (!endpoints) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSuccess("");
    setSelectedIds(new Set());
    fetch(`${API}${endpoints.base}`, {
      credentials: "include", headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Load failed (${r.status})`))))
      .then((data) => {
        if (cancelled) return;
        const list = data.images || [];
        setImages(list);
        setInitialImages(list);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [endpoints, headers]);

  // Drop any selection that no longer points at a visible image.
  const visibleIds = useMemo(() => new Set(images.map((i) => i.id)), [images]);
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set();
      for (const id of prev) if (visibleIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [visibleIds]);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(images.map((i) => i.id)));
  }, [images]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const onBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !endpoints) return;
    const ok = window.confirm(
      `Delete ${ids.length} image${ids.length > 1 ? "s" : ""} from this gallery? ` +
      `They'll be removed from the page immediately.`
    );
    if (!ok) return;
    setBulkDeleting(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(
        `${API}${endpoints.base}/images/bulk-delete`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        }
      );
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Bulk delete failed: ${msg.slice(0, 200)}` : `Bulk delete failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      const next = data.images || [];
      setImages(next);
      setInitialImages(next);
      setSelectedIds(new Set());
      setSuccess(`${ids.length} image${ids.length > 1 ? "s" : ""} deleted.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, endpoints, headers]);

  const onUploadFiles = useCallback(async (files) => {
    if (!files || !files.length || !endpoints) return;
    if (maxImages != null) {
      const remaining = Math.max(0, maxImages - images.length);
      if (remaining <= 0) {
        setError(`This gallery is capped at ${maxImages} image${maxImages === 1 ? "" : "s"}. Remove one before uploading more.`);
        return;
      }
      if (files.length > remaining) {
        setError(`Only ${remaining} more image${remaining === 1 ? "" : "s"} allowed (cap is ${maxImages}). Trim your selection and try again.`);
        return;
      }
    }
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    setError("");
    setSuccess("");
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(
          `${API}${endpoints.base}/upload`,
          { method: "POST", headers, body: fd }
        );
        if (!res.ok) {
          const msg = await safeReadBody(res);
          throw new Error(msg ? `Upload failed: ${msg.slice(0, 200)}` : `Upload failed (HTTP ${res.status})`);
        }
        const data = await res.json();
        setImages((prev) => [...prev, data.image]);
        setInitialImages((prev) => [...prev, data.image]);
        setUploadProgress({ done: i + 1, total: files.length });
      }
      setSuccess(`${files.length} image${files.length > 1 ? "s" : ""} uploaded.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  }, [endpoints, headers, images.length, maxImages]);

  const onUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    onUploadFiles(files);
  }, [onUploadFiles]);

  // Add several existing Media-Library assets to this gallery in one
  // call — no file bytes leave the browser, the backend just references the
  // same storage URLs and assigns fresh gallery-image ids.
  const onAddFromLibrary = useCallback(async (assets) => {
    if (!assets || !assets.length || !endpoints) return;
    if (maxImages != null) {
      const remaining = Math.max(0, maxImages - images.length);
      if (remaining <= 0) {
        setPickerOpen(false);
        setError(`This gallery is capped at ${maxImages} image${maxImages === 1 ? "" : "s"}. Remove one first.`);
        return;
      }
      if (assets.length > remaining) {
        setPickerOpen(false);
        setError(`Only ${remaining} more image${remaining === 1 ? "" : "s"} allowed (cap is ${maxImages}). Trim your selection and try again.`);
        return;
      }
    }
    setPickerOpen(false);
    setAddingFromLibrary(true);
    setError("");
    setSuccess("");
    try {
      const asset_ids = assets.map((a) => a.id).filter(Boolean);
      const res = await fetch(
        `${API}${endpoints.base}/images/from-library`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ asset_ids }),
        }
      );
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Add from library failed: ${msg.slice(0, 200)}` : `Add from library failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      const next = data.images || [];
      setImages(next);
      setInitialImages(next);
      const count = (data.added || []).length || asset_ids.length;
      setSuccess(`${count} image${count > 1 ? "s" : ""} added from the library.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingFromLibrary(false);
    }
  }, [endpoints, headers, images.length, maxImages]);

  // Add several Bunny Storage files to this gallery in one call. Same
  // shape as `onAddFromLibrary` but posts `items: [{url, alt, source,
  // source_ref}]` so the backend can record the Bunny origin without
  // creating a duplicate Media-Library asset.
  const onAddFromBunny = useCallback(async (files) => {
    if (!files || !files.length || !endpoints) return;
    if (maxImages != null) {
      const remaining = Math.max(0, maxImages - images.length);
      if (remaining <= 0) {
        setBunnyOpen(false);
        setError(`This gallery is capped at ${maxImages} image${maxImages === 1 ? "" : "s"}. Remove one first.`);
        return;
      }
      if (files.length > remaining) {
        setBunnyOpen(false);
        setError(`Only ${remaining} more image${remaining === 1 ? "" : "s"} allowed (cap is ${maxImages}). Trim your selection and try again.`);
        return;
      }
    }
    setBunnyOpen(false);
    setAddingFromBunny(true);
    setError("");
    setSuccess("");
    try {
      const items = files
        .filter((f) => f && f.public_url)
        .map((f) => ({
          url: f.public_url,
          alt: f.name || "",
          source: "bunny_storage",
          source_ref: f.path || "",
        }));
      if (!items.length) throw new Error("No usable files in the selection.");
      const res = await fetch(
        `${API}${endpoints.base}/images/from-urls`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        }
      );
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Add from Bunny failed: ${msg.slice(0, 200)}` : `Add from Bunny failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      const next = data.images || [];
      setImages(next);
      setInitialImages(next);
      const count = (data.added || []).length || items.length;
      setSuccess(`${count} image${count > 1 ? "s" : ""} added from Bunny Storage.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingFromBunny(false);
    }
  }, [endpoints, headers, images.length, maxImages]);

  const onDelete = useCallback(async (imageId) => {
    if (!endpoints) return;
    if (!window.confirm("Delete this image? It will be removed from this gallery.")) return;
    setError("");
    setSuccess("");
    try {
      const res = await fetch(
        `${API}${endpoints.base}/images/${imageId}`,
        { method: "DELETE", headers }
      );
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      const data = await res.json();
      setImages(data.images || []);
      setInitialImages(data.images || []);
      setSuccess("Image deleted.");
    } catch (err) {
      setError(err.message);
    }
  }, [endpoints, headers]);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setImages((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const onSave = useCallback(async () => {
    if (!endpoints) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(
        `${API}${endpoints.base}`,
        {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ images }),
        }
      );
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setInitialImages(images);
      setSuccess("Gallery order saved.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [endpoints, images, headers]);

  const atCap = maxImages != null && images.length >= maxImages;
  const remainingSlots = maxImages != null ? Math.max(0, maxImages - images.length) : null;

  if (!target) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-[#5C5248] py-32 px-6 text-center">
        <ImageIcon className="w-10 h-10 mb-4" strokeWidth={1.4} />
        <p className="font-serif-x text-2xl text-[#2C2621]">Select a gallery</p>
        <p className="mt-2 text-sm">
          Pick a property on the left, then choose a room, the <em>About the Hotel</em> section,
          or one of the property&apos;s services to manage its images.
        </p>
      </div>
    );
  }

  const TargetIcon = target.icon || BedDouble;

  return (
    <FileDropZone
      onFiles={onUploadFiles}
      disabled={uploading}
      className="flex-1 min-w-0"
      overlayLabel="Drop images to add to this gallery"
      testid="gallery-editor"
    >
      <header className="px-6 md:px-10 py-7 border-b border-[#2C2621]/10 bg-white">
        <p className="overline text-[#C16542] flex items-center gap-2" data-testid="gallery-editor-eyebrow">
          <TargetIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
          {target.hotelName}
        </p>
        <h2 className="font-serif-x text-3xl md:text-4xl mt-2 tracking-tight" data-testid="gallery-editor-title">
          {target.label}
        </h2>
        {target.subtitle && (
          <p className="mt-2 text-sm text-[#5C5248]" data-testid="gallery-editor-subtitle">
            {target.subtitle}
          </p>
        )}
      </header>

      <div className="px-6 md:px-10 py-6 border-b border-[#2C2621]/10 flex flex-wrap items-center gap-3">
        <label
          className={`inline-flex items-center gap-2 ${atCap ? "bg-[#C16542]/40 cursor-not-allowed" : "bg-[#C16542] hover:bg-[#A35133] cursor-pointer"} text-[#FDFBF7] px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors`}
          data-testid="gallery-upload-trigger"
          title={atCap ? `Gallery cap reached (${maxImages})` : undefined}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.6} />}
          {uploading ? "Uploading…" : atCap ? "Upload (cap reached)" : "Upload images"}
          <input
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
            className="hidden"
            onChange={onUpload}
            disabled={uploading || atCap}
            data-testid="gallery-upload-input"
          />
        </label>
        <button
          type="button"
          onClick={() => setBunnyOpen(true)}
          disabled={addingFromBunny || addingFromLibrary || uploading || atCap}
          className="inline-flex items-center gap-2 border border-[#C16542] text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#C16542] px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
          data-testid="gallery-select-from-bunny"
          title={atCap ? `Gallery cap reached (${maxImages})` : "Browse the Bunny.net Storage Zone and reference images directly (no re-upload)"}
        >
          {addingFromBunny
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Database className="w-4 h-4" strokeWidth={1.6} />}
          {addingFromBunny ? "Adding…" : "Select from Bunny Store"}
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={addingFromLibrary || addingFromBunny || uploading || atCap}
          className="inline-flex items-center gap-2 border border-[#C16542] text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#C16542] px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
          data-testid="gallery-select-from-library"
          title={atCap ? `Gallery cap reached (${maxImages})` : "Add existing images from the Media Library — no upload needed"}
        >
          {addingFromLibrary
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <LibraryBig className="w-4 h-4" strokeWidth={1.6} />}
          {addingFromLibrary ? "Adding…" : "Select from Library"}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#2C2621] hover:bg-[#2C2621] hover:text-[#FDFBF7] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#2C2621] px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
          data-testid="gallery-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.6} />}
          {dirty ? "Save order" : "Saved"}
        </button>
        <span className="text-[11px] tracking-[0.18em] uppercase text-[#5C5248]" data-testid="gallery-image-counter">
          {maxImages != null
            ? `${images.length} / ${maxImages} image${maxImages === 1 ? "" : "s"}`
            : `${images.length} image${images.length === 1 ? "" : "s"}`}
        </span>
        {uploading && (
          <UploadProgress done={uploadProgress.done} total={uploadProgress.total} />
        )}
        {images.length > 0 && (
          <button
            type="button"
            onClick={selectedIds.size === images.length ? clearSelection : selectAll}
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542]"
            data-testid="gallery-select-all-toggle"
          >
            {selectedIds.size === images.length
              ? <><CheckSquare className="w-3.5 h-3.5" /> Deselect all</>
              : <><Square className="w-3.5 h-3.5" /> Select all</>}
          </button>
        )}
        {error && <span className="text-sm text-[#C16542]" data-testid="gallery-error">{error}</span>}
        {success && !error && <span className="text-sm text-[#5A6B4F]" data-testid="gallery-success">{success}</span>}
      </div>

      <div className="px-6 md:px-10 py-8">
        {loading ? (
          <div className="flex items-center gap-3 text-[#5C5248]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading gallery…
          </div>
        ) : images.length === 0 ? (
          <div className="border border-dashed border-[#2C2621]/20 py-20 text-center text-[#5C5248]" data-testid="gallery-empty">
            <ImagePlus className="w-8 h-8 mx-auto mb-3" strokeWidth={1.4} />
            <p className="font-serif-x text-xl text-[#2C2621]">No images yet</p>
            <p className="mt-1 text-sm">
              Drag and drop images here, or click <strong>Upload images</strong> to add the first photo.
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="gallery-grid">
                {images.map((img) => (
                  <SortableImageCard
                    key={img.id}
                    image={img}
                    onDelete={onDelete}
                    isSelected={selectedIds.has(img.id)}
                    onToggleSelect={toggleSelected}
                    anySelected={selectedIds.size > 0}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] bg-[#2C2621] text-[#FDFBF7] shadow-xl border border-[#FDFBF7]/10 flex items-center gap-2 px-3 py-2 max-w-[95vw]"
          role="region"
          aria-label="Bulk selection actions"
          data-testid="gallery-bulk-action-bar"
        >
          <span className="overline text-[#FDFBF7]/70 px-2" data-testid="gallery-bulk-count">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={selectAll}
            disabled={selectedIds.size === images.length || images.length === 0}
            className="text-[11px] tracking-[0.22em] uppercase px-3 py-2 hover:bg-[#FDFBF7]/10 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="gallery-bulk-select-all"
          >
            <CheckSquare className="w-3.5 h-3.5" /> Select all
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[11px] tracking-[0.22em] uppercase px-3 py-2 hover:bg-[#FDFBF7]/10 inline-flex items-center gap-1.5"
            data-testid="gallery-bulk-clear"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
          <button
            type="button"
            onClick={onBulkDelete}
            disabled={bulkDeleting}
            className="text-[11px] tracking-[0.22em] uppercase px-4 py-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="gallery-bulk-delete"
          >
            {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {bulkDeleting ? "Deleting…" : "Delete selected"}
          </button>
        </div>
      )}
      {pickerOpen && (
        <MediaPicker
          multiple
          title={endpoints?.pickerTitle || "Add images"}
          onSelect={onAddFromLibrary}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {bunnyOpen && (
        <BunnyStoragePicker
          multiple
          title={endpoints?.pickerTitle ? `${endpoints.pickerTitle} — Bunny` : "Add from Bunny Storage"}
          onSelect={onAddFromBunny}
          onClose={() => setBunnyOpen(false)}
        />
      )}
    </FileDropZone>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Hero editor — single-image panel used by property sections (independent
// from the gallery editor below it). Upload replaces atomically.
// ────────────────────────────────────────────────────────────────────────
const HeroEditor = ({ target }) => {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bunnyOpen, setBunnyOpen] = useState(false);

  const endpoints = useMemo(() => buildEndpoints(target), [target]);
  // Auth via httpOnly cookie — headers kept for callers that spread it in.
  const headers = useMemo(() => ({}), []);

  useEffect(() => {
    if (!endpoints) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSuccess("");
    fetch(`${API}${endpoints.base}`, {
      credentials: "include", headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Load failed (${r.status})`))))
      .then((data) => {
        if (cancelled) return;
        setImage((data.images || [])[0] || null);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [endpoints, headers]);

  const onUpload = useCallback(async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file || !endpoints) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}${endpoints.base}/upload`, {
      credentials: "include",
        method: "POST", headers, body: fd,
      });
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Upload failed: ${msg.slice(0, 200)}` : `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setImage(data.image);
      setSuccess(image ? "Hero image replaced." : "Hero image uploaded.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [endpoints, headers, image]);

  const onPickFromLibrary = useCallback(async (asset) => {
    setPickerOpen(false);
    if (!asset || !endpoints) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${API}${endpoints.base}/images/from-library`, {
      credentials: "include",
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ asset_ids: [asset.id] }),
      });
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Pick failed: ${msg.slice(0, 200)}` : `Pick failed (${res.status})`);
      }
      const data = await res.json();
      setImage((data.images || [])[0] || null);
      setSuccess("Hero image updated from the library.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [endpoints, headers]);

  // Pick a hero image directly from Bunny Storage (no upload, no
  // Media-Library duplication — just stores the public CDN URL).
  const onPickFromBunny = useCallback(async (file) => {
    setBunnyOpen(false);
    const f = Array.isArray(file) ? file[0] : file;
    if (!f || !f.public_url || !endpoints) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${API}${endpoints.base}/images/from-urls`, {
      credentials: "include",
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{
            url: f.public_url,
            alt: f.name || "",
            source: "bunny_storage",
            source_ref: f.path || "",
          }],
        }),
      });
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Pick failed: ${msg.slice(0, 200)}` : `Pick failed (${res.status})`);
      }
      const data = await res.json();
      setImage((data.images || [])[0] || null);
      setSuccess("Hero image updated from Bunny Storage.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [endpoints, headers]);

  const onDelete = useCallback(async () => {
    if (!image || !endpoints) return;
    if (!window.confirm("Remove this hero image? It will no longer appear on the page.")) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${API}${endpoints.base}/images/${image.id}`, {
      credentials: "include",
        method: "DELETE", headers,
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setImage(null);
      setSuccess("Hero image removed.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [image, endpoints, headers]);

  return (
    <section
      className="px-6 md:px-10 py-7 border-b border-[#2C2621]/10 bg-[#FDFBF7]/60"
      data-testid="hero-editor"
    >
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <div>
          <p className="overline text-[#5C5248]">Hero · single image</p>
          <p className="text-xs text-[#5C5248] mt-1">
            Banner photo featured at the top of the {target.sectionLabel} page.
            Uploads <strong>replace</strong> the current hero — the gallery below is untouched.
          </p>
        </div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-[#5C5248]" />}
      </div>
      <div className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-5 items-stretch">
        <div
          className="relative aspect-[16/9] bg-[#F2EBE1] border border-[#2C2621]/10 overflow-hidden"
          data-testid="hero-preview"
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center text-[#5C5248]">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : image ? (
            <img
              src={resolveImageUrl(image.url)}
              alt={`${target.sectionLabel} hero`}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-image" loading="lazy" decoding="async"/>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5C5248]">
              <ImageDown className="w-8 h-8 mb-3" strokeWidth={1.4} />
              <p className="text-sm">No hero image yet.</p>
            </div>
          )}
        </div>
        <div className="flex md:flex-col gap-3 flex-wrap md:min-w-[260px]">
          <label
            className="inline-flex items-center gap-2 bg-[#2C2621] hover:bg-[#1A1513] disabled:opacity-40 text-[#FDFBF7] px-5 py-3 text-[10px] tracking-[0.25em] uppercase cursor-pointer transition-colors"
            data-testid="hero-upload-trigger"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.6} />}
            {busy ? "Working…" : image ? "Replace hero" : "Upload hero"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
              className="hidden"
              onChange={onUpload}
              disabled={busy}
              data-testid="hero-upload-input"
            />
          </label>
          <button
            type="button"
            onClick={() => setBunnyOpen(true)}
            disabled={busy}
            className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 px-5 py-3 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="hero-pick-bunny"
            title="Pick the hero directly from Bunny Storage"
          >
            <Database className="w-4 h-4" strokeWidth={1.6} />
            Select from Bunny Store
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={busy}
            className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 px-5 py-3 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="hero-pick-library"
            title="Pick the hero from the Media Library"
          >
            <LibraryBig className="w-4 h-4" strokeWidth={1.6} />
            Select from Library
          </button>
          {image && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="inline-flex items-center gap-2 border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 px-5 py-3 text-[10px] tracking-[0.25em] uppercase transition-colors"
              data-testid="hero-delete"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.6} />
              Remove hero
            </button>
          )}
        </div>
      </div>
      {(error || success) && (
        <p
          className={`mt-3 text-sm ${error ? "text-[#C16542]" : "text-[#5A6B4F]"}`}
          data-testid={error ? "hero-error" : "hero-success"}
        >
          {error || success}
        </p>
      )}
      {pickerOpen && (
        <MediaPicker
          title={`Pick hero image for ${target.sectionLabel}`}
          confirmLabel="Use as hero"
          onSelect={onPickFromLibrary}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {bunnyOpen && (
        <BunnyStoragePicker
          title={`Pick hero from Bunny Storage`}
          onSelect={onPickFromBunny}
          onClose={() => setBunnyOpen(false)}
        />
      )}
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Property section editor — stacks Hero + Gallery panels. Each uses
// completely independent backend endpoints so they can never affect
// each other.
// ────────────────────────────────────────────────────────────────────────
const PropertySectionEditor = ({ section }) => {
  const heroTarget = useMemo(() => ({
    ...section,
    kind: "property-hero",
    label: `${section.sectionLabel} · Hero`,
    subtitle: "Single image · uploads replace · independent of the gallery below.",
  }), [section]);
  const galleryTarget = useMemo(() => ({
    ...section,
    kind: "property-gallery",
    label: `${section.sectionLabel} · Gallery`,
    subtitle: "Multi-image collection · drag to reorder · independent of the hero above.",
  }), [section]);
  return (
    <div className="flex-1 min-w-0" data-testid="property-section-editor">
      <header className="px-6 md:px-10 py-7 border-b border-[#2C2621]/10 bg-white">
        <p className="overline text-[#C16542] flex items-center gap-2">
          {section.icon && <section.icon className="w-3.5 h-3.5" strokeWidth={1.6} />}
          {section.hotelName}
        </p>
        <h2 className="font-serif-x text-3xl md:text-4xl mt-2 tracking-tight" data-testid="property-section-title">
          {section.sectionLabel}
        </h2>
        <p className="mt-2 text-sm text-[#5C5248]">
          Two independent image collections: a single hero image at the top of the page,
          and a multi-image gallery shown inside the content.
        </p>
      </header>
      <HeroEditor target={heroTarget} />
      <GalleryEditor target={galleryTarget} />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────
const AdminGalleriesPage = () => {
  // null = still checking cookie, false = not authed, true = authed
  const [authState, setAuthState] = useState(null);
  const [tree] = useState(() => buildHotelRoomTree());
  const [deletedSet, setDeletedSet] = useState(() => new Set()); // "hotelSlug/roomSlug"
  const [selected, setSelected] = useState(null); // target object
  const [filter, setFilter] = useState("");

  const onLogout = async () => {
    try {
      await fetch(`${API}/api/admin/session/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch { /* ignore */ }
    setAuthState(false);
  };

  // Build a target for a room — used both for tree clicks and for keeping
  // the editor in sync. Captures the hotel name as `hotelName` plus
  // surface/capacity for the subtitle line.
  const buildRoomTarget = useCallback((hotelNode, room) => ({
    kind: "room",
    hotelSlug: hotelNode.hotel.slug,
    hotelName: hotelNode.hotel.name,
    slug: room.slug,
    label: pick(room.name),
    subtitle: [
      typeof room.surface === "string" ? room.surface : pick(room.surface),
      pick(room.capacity),
    ].filter(Boolean).join(" · "),
    icon: BedDouble,
  }), []);

  // Build a section target — the wrapping `PropertySectionEditor` will
  // derive separate "hero" and "gallery" sub-targets from this.
  const buildSectionTarget = useCallback((hotelNode, section) => ({
    kind: "property-section",
    hotelSlug: hotelNode.hotel.slug,
    hotelName: hotelNode.hotel.name,
    sectionSlug: section.slug,
    sectionLabel: section.label,
    icon: section.icon,
  }), []);

  // Verify cookie session on mount
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/admin/session/verify`, {
      method: "GET",
      credentials: "include",
    })
      .then((r) => {
        if (!cancelled) setAuthState(r.ok);
      })
      .catch(() => {
        if (!cancelled) setAuthState(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Deep-link support: `?hotel=<slug>&room=<slug>` pre-selects the matching
  // room gallery on mount so the "Manage gallery" CTA on each hotel detail
  // page can drop the admin straight into the correct editor. Runs once
  // per URL change.
  useEffect(() => {
    if (authState !== true) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const hotelSlug = params.get("hotel");
    const roomSlug = params.get("room");
    if (!hotelSlug || !roomSlug) return;
    const hotelNode = tree.find((n) => n.hotel && n.hotel.slug === hotelSlug);
    if (!hotelNode) return;
    const room = (hotelNode.rooms || []).find((r) => r.slug === roomSlug);
    if (!room) return;
    setSelected(buildRoomTarget(hotelNode, room));
  }, [authState, tree, buildRoomTarget]);

  // Load the global CMS state once so we can hide permanently-deleted rooms
  // from the sidebar. The public endpoint is unauthenticated.
  useEffect(() => {
    fetch(`${API}/api/hotels/cms`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        const set = new Set();
        for (const it of data.items || []) {
          if (it.is_deleted || it.is_hidden) {
            set.add(`${it.hotel_slug}/${it.room_slug}`);
          }
        }
        setDeletedSet(set);
      })
      .catch(() => {/* silent — overrides optional */});
  }, []);

  if (authState === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#5C5248]" data-testid="admin-galleries-checking">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking access…
      </div>
    );
  }
  if (authState === false) return <PasswordGate onUnlock={() => setAuthState(true)} />;

  const liveTree = tree.map((node) => ({
    ...node,
    rooms: node.rooms.filter((r) => !deletedSet.has(`${node.hotel.slug}/${r.slug}`)),
  }));

  const filtered = filter
    ? liveTree
        .map((node) => {
          const term = filter.toLowerCase();
          const hotelMatch = node.hotel.name.toLowerCase().includes(term);
          return {
            ...node,
            rooms: node.rooms.filter((r) => {
              const txt = (pick(r.name) + " " + node.hotel.name).toLowerCase();
              return txt.includes(term);
            }),
            _keepForSections: hotelMatch,
          };
        })
        .filter((n) => n.rooms.length > 0 || n._keepForSections)
    : liveTree;

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-galleries-page">
      <header className="px-6 md:px-10 py-8 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-[#C16542]">Grup Xaluca · Admin</p>
            <h1 className="font-serif-x text-4xl md:text-5xl mt-2 tracking-tight">
              Property galleries
            </h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              Manage every photo collection across the group — room galleries, the <em>About the Hotel</em> hero &amp; gallery, plus an independent <em>hero</em> + <em>gallery</em> for every service (Spa, Restaurant, Pool, Gym…). Hero and gallery for the same section are stored separately — editing one never touches the other.
            </p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="admin-galleries-logout"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} />
            Lock
          </button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row min-h-[calc(100vh-180px)]">
        {/* SIDEBAR */}
        <aside className="lg:w-[360px] xl:w-[400px] border-b lg:border-b-0 lg:border-r border-[#2C2621]/10 bg-white" data-testid="admin-galleries-sidebar">
          <div className="px-5 py-5 border-b border-[#2C2621]/10">
            <label className="relative block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5248]" strokeWidth={1.6} />
              <input
                type="search"
                placeholder="Search properties or rooms…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542]"
                data-testid="admin-galleries-search"
              />
            </label>
          </div>
          <nav className="max-h-[calc(100vh-260px)] overflow-y-auto py-2" data-testid="admin-galleries-tree">
            {filtered.map((node) => (
              <div key={node.hotel.slug} className="px-5 py-4 border-b border-[#2C2621]/5">
                <p className="font-serif-x text-lg leading-tight text-[#2C2621]" data-testid={`tree-hotel-${node.hotel.slug}`}>
                  {node.hotel.name}
                </p>
                {node.hotel.location && (
                  <p className="mt-0.5 text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                    {node.hotel.location}
                  </p>
                )}

                {/* Hotel card images — dedicated thumbnail strip shown on
                    the homepage property cards. First image is the big
                    hero; the rest populate the horizontal thumb strip
                    (only visible when ≥ 2 images are uploaded). Capped
                    at 10 images per hotel. */}
                <p className="mt-4 text-[9px] tracking-[0.28em] uppercase text-[#5C5248]/70">
                  Homepage card
                </p>
                <ul className="mt-1.5 space-y-1">
                  {(() => {
                    const active =
                      selected &&
                      selected.kind === "property-gallery" &&
                      selected.hotelSlug === node.hotel.slug &&
                      selected.sectionSlug === "card";
                    return (
                      <li>
                        <button
                          type="button"
                          onClick={() => setSelected({
                            kind: "property-gallery",
                            hotelSlug: node.hotel.slug,
                            hotelName: node.hotel.name,
                            sectionSlug: "card",
                            sectionLabel: "Hotel card images",
                            label: "Hotel card images",
                            subtitle: "Shared across every card surface (Home widget · /contact · /booking). Up to 10 photos · first image is the big hero on the homepage card · the rest fill the /contact and /booking carousels (first 8 used, in order).",
                            icon: ImageIcon,
                            maxImages: 10,
                          })}
                          className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                            active
                              ? "bg-[#2C2621] text-[#FDFBF7]"
                              : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                          }`}
                          data-testid={`tree-card-${node.hotel.slug}`}
                        >
                          <ImageIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />
                          <span className="truncate">Hotel card images</span>
                          <span className={`ml-auto text-[9px] tracking-[0.22em] uppercase shrink-0 ${active ? "text-[#D4A373]" : "text-[#5C5248]"}`}>
                            Max 10
                          </span>
                        </button>
                      </li>
                    );
                  })()}
                </ul>

                {/* Property sections — About the Hotel + each service slug,
                    auto-discovered from `hotelDetails.js` per hotel. */}
                <p className="mt-4 text-[9px] tracking-[0.28em] uppercase text-[#5C5248]/70">
                  Property sections
                </p>
                <ul className="mt-1.5 space-y-1">
                  {buildPropertySections(node.hotel.slug).map((section) => {
                    const Icon = section.icon;
                    const active =
                      selected &&
                      selected.kind === "property-section" &&
                      selected.hotelSlug === node.hotel.slug &&
                      selected.sectionSlug === section.slug;
                    return (
                      <li key={section.slug}>
                        <button
                          type="button"
                          onClick={() => setSelected(buildSectionTarget(node, section))}
                          className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                            active
                              ? "bg-[#2C2621] text-[#FDFBF7]"
                              : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                          }`}
                          data-testid={`tree-section-${node.hotel.slug}-${section.slug}`}
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />
                          <span className="truncate">{section.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Room galleries */}
                <p className="mt-4 text-[9px] tracking-[0.28em] uppercase text-[#5C5248]/70">
                  Rooms
                </p>
                {node.rooms.length === 0 ? (
                  <p className="mt-1.5 text-xs italic text-[#5C5248]">No rooms defined.</p>
                ) : (() => {
                  // If any room carries a `camp` slug and the hotel has a
                  // matching `camps` list, group rooms under a sub-header
                  // per camp so admins can see the same three-way split
                  // the public page exposes via tabs. Falls back to a
                  // flat list for every other hotel.
                  const hasCamps = node.rooms.some((r) => !!r.camp) && Array.isArray(node.camps) && node.camps.length > 0;
                  const roomsWithoutCamp = node.rooms.filter((r) => !r.camp);
                  const renderRoomRow = (room) => {
                    const active =
                      selected &&
                      selected.kind === "room" &&
                      selected.hotelSlug === node.hotel.slug &&
                      selected.slug === room.slug;
                    return (
                      <li key={room.slug}>
                        <button
                          type="button"
                          onClick={() => setSelected(buildRoomTarget(node, room))}
                          className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 text-sm transition-colors ${
                            active
                              ? "bg-[#2C2621] text-[#FDFBF7]"
                              : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                          }`}
                          data-testid={`tree-room-${node.hotel.slug}-${room.slug}`}
                        >
                          <span className="truncate">
                            {pick(room.name)}
                            {room.variantLabel && (
                              <span className={active ? "text-[#D4A373]" : "text-[#5C5248]"}>
                                {" · "}{pick(room.variantLabel)}
                              </span>
                            )}
                          </span>
                          {room.surface && (
                            <span className={`text-[10px] tracking-[0.18em] uppercase shrink-0 ${active ? "text-[#D4A373]" : "text-[#5C5248]"}`}>
                              {(typeof room.surface === "string" ? room.surface : pick(room.surface)).split("·")[0].trim()}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  };
                  if (!hasCamps) {
                    return (
                      <ul className="mt-1.5 space-y-1">
                        {node.rooms.map((room) => renderRoomRow(room))}
                      </ul>
                    );
                  }
                  return (
                    <div className="mt-1.5 space-y-3" data-testid={`tree-rooms-by-camp-${node.hotel.slug}`}>
                      {node.camps.map((camp) => {
                        const roomsInCamp = node.rooms.filter((r) => r.camp === camp.slug);
                        if (roomsInCamp.length === 0) return null;
                        return (
                          <div key={`camp-group-${camp.slug}`} data-testid={`tree-camp-group-${camp.slug}`}>
                            <p className="px-3 mt-1 text-[9px] tracking-[0.22em] uppercase text-[#C16542]">
                              {pick(camp.name)}
                            </p>
                            <ul className="mt-1 space-y-1">
                              {roomsInCamp.map((room) => renderRoomRow(room))}
                            </ul>
                          </div>
                        );
                      })}
                      {roomsWithoutCamp.length > 0 && (
                        <div data-testid={`tree-camp-group-unassigned-${node.hotel.slug}`}>
                          <p className="px-3 mt-1 text-[9px] tracking-[0.22em] uppercase text-[#5C5248]/70">
                            Unassigned
                          </p>
                          <ul className="mt-1 space-y-1">
                            {roomsWithoutCamp.map((room) => renderRoomRow(room))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}

            {/* EXPERIENCES — same property-galleries API, namespaced with
                an "experience-" prefix so they live alongside hotels without
                colliding. Only an "About this experience" section is exposed
                because each experience page uses a single 5-tile grid. */}
            {(() => {
              const filteredExperiences = filter
                ? EXPERIENCES.filter((e) => {
                    const term = filter.toLowerCase();
                    return (
                      e.key.toLowerCase().includes(term) ||
                      (e.title?.en || "").toLowerCase().includes(term) ||
                      (e.title?.fr || "").toLowerCase().includes(term) ||
                      (e.title?.es || "").toLowerCase().includes(term)
                    );
                  })
                : EXPERIENCES;
              if (!filteredExperiences.length) return null;
              return (
                <div className="px-5 py-4 border-b border-[#2C2621]/5" data-testid="tree-experiences-group">
                  <p className="font-serif-x text-lg leading-tight text-[#2C2621]">
                    Experiences
                  </p>
                  <p className="mt-0.5 text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                    Xaluca Originals
                  </p>
                  <p className="mt-4 text-[9px] tracking-[0.28em] uppercase text-[#5C5248]/70">
                    Image sections
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {filteredExperiences.map((exp) => {
                      const expSlug = `experience-${exp.key}`;
                      const active =
                        selected &&
                        selected.kind === "property-section" &&
                        selected.hotelSlug === expSlug &&
                        selected.sectionSlug === "about";
                      return (
                        <li key={exp.key}>
                          <button
                            type="button"
                            onClick={() =>
                              setSelected({
                                kind: "property-section",
                                hotelSlug: expSlug,
                                hotelName: exp.title?.en || exp.key,
                                sectionSlug: "about",
                                sectionLabel: "About this experience",
                                icon: BookOpen,
                              })
                            }
                            className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                              active
                                ? "bg-[#2C2621] text-[#FDFBF7]"
                                : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                            }`}
                            data-testid={`tree-experience-${exp.key}`}
                          >
                            <BookOpen className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />
                            <span className="truncate">{exp.title?.en || exp.key}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })()}
          </nav>
        </aside>

        {/* EDITOR */}
        {selected?.kind === "property-section" ? (
          <PropertySectionEditor section={selected} />
        ) : selected?.kind === "property-gallery" ? (
          <GalleryEditor
            target={selected}
            maxImages={selected.maxImages || null}
          />
        ) : (
          <GalleryEditor target={selected} />
        )}
      </div>
    </section>
  );
};

export default AdminGalleriesPage;
